import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestViewMessageOverview,
  requestViewMessages,
  requestCreateMessage,
  requestDeleteMessage
} from './support.actions';
import {
  getViewMessageOverviewRequest,
  getViewMessagesRequest,
  getCreateMessageRequest,
  getDeleteMessageRequest
} from './support.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setMessageOverviews, setMessageOwner, setMessages } from '../../slices/programme/support/support.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performViewMessageOverviewRequest({ payload }) {
  const { programmeExternalReference, showSpinner, onSuccess } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Message Overview'));
    }

    const [endpoint, options] = getViewMessageOverviewRequest(programmeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    if (showSpinner) {
      yield put(removeSpinner('Retrieving Message Overview'));
    }

    yield put(setMessageOverviews(data.overviewSupportMessages));
  } catch (error) {
    const message = getErrorMessage(error);

    if (showSpinner) {
      yield put(removeSpinner('Retrieving Message Overview'));
    }

    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Message Overview'));
  }
}

export function* watchForViewMessageOverviewRequest() {
  yield takeLatest(requestViewMessageOverview.type, performViewMessageOverviewRequest);
}

export function* perfromViewMessagesRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, page, size, showSpinner, onSuccess } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Messages'));
    }

    const [endpoint, options] = getViewMessagesRequest(
      programmeExternalReference,
      systemUserExternalReference,
      page,
      size
    );

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    if (showSpinner) {
      yield put(removeSpinner('Retrieving Messages'));
    }

    yield put(setMessages(data.supportMessages));
    yield put(setMessageOwner(data.ownerSystemUserName));
  } catch (error) {
    const message = getErrorMessage(error);

    if (showSpinner) {
      yield put(removeSpinner('Retrieving Messages'));
    }

    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewMessagesRequest() {
  yield takeLatest(requestViewMessages.type, perfromViewMessagesRequest);
}

export function* performCreateMessageRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    const [endpoint, options] = getCreateMessageRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateMessageRequest() {
  yield takeLatest(requestCreateMessage.type, performCreateMessageRequest);
}

export function* performDeleteMessageRequest({ payload }) {
  const { programmeExternalReference, systemUserExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Deleting Messages'));
    const [endpoint, options] = getDeleteMessageRequest(programmeExternalReference, systemUserExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner('Deleting Messages'));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewMessageOverview({ programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner('Deleting Messages'));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeleteMessageRequest() {
  yield takeLatest(requestDeleteMessage.type, performDeleteMessageRequest);
}

export default function* supportSaga() {
  yield all([
    watchForViewMessageOverviewRequest(),
    watchForViewMessagesRequest(),
    watchForCreateMessageRequest(),
    watchForDeleteMessageRequest()
  ]);
}
